// src/components/DeviceManagement/DeviceManagementContainer.js
import React from "react";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import DeviceState from "../../DeviceState";
import DeviceSettings from "../../DeviceSettings";
import LastActionStatus from "../../LastActionStatus";
import DeviceActions from "../../DeviceActions";
import SimplifiedDeviceActions from "../../SimplifiedDeviceActions";
import SimplifiedDeviceState from "../../SimplifiedDeviceState";
import LoadingModal from "../../LoadingModal";
import "./DeviceManagementContainer.css";

/**
 * Container component for device management UI
 *
 * Orchestrates the display of device state, actions, and credentials
 * Supports both simplified and detailed view modes
 * Detailed mode supports drag-and-drop reordering of sections
 *
 * @param {Object} props - Component props
 * @param {string} props.deviceId - Currently selected device ID
 * @param {Object} props.deviceState - Device state object
 * @param {string} props.viewMode - Display mode ('simplified' or 'detailed')
 * @param {Array} props.actionHistory - Array of device actions
 * @param {number} props.actionHistoryLimit - Number of actions to display
 * @param {Function} props.onActionHistoryLimitChange - Callback when limit changes
 * @param {string} props.loadingAction - Currently executing action type (or null)
 * @param {Function} props.onExecuteAction - Callback to execute device action
 * @param {Function} props.onDownloadSettings - Callback to download device settings
 * @param {Function} props.onUpdateFirmware - Callback to initiate firmware update
 * @param {boolean} props.isDownloadingSettings - Whether settings download is in progress
 * @param {boolean} props.isSyncing - Whether data sync is in progress
 * @param {Function} props.onEditDisplayNameClick - Callback to edit device display name
 * @param {number} props.connectivityStatusUpdateTime - Timestamp of last connectivity update
 * @param {Object} props.collapseStates - Collapse state for each section
 * @param {Object} props.onToggleCollapse - Callbacks to toggle collapse states
 * @param {Array} props.components - Component ordering array (for detailed view)
 * @param {Function} props.onDragEnd - Callback for drag-and-drop reordering
 * @param {React.ReactNode} props.credentialsSection - Rendered credentials section
 */
const DeviceManagementContainer = ({
  deviceId,
  deviceState,
  viewMode,
  actionHistory,
  actionHistoryLimit,
  onActionHistoryLimitChange,
  loadingAction,
  onExecuteAction,
  onDownloadSettings,
  onUpdateFirmware,
  isDownloadingSettings,
  isSyncing,
  onEditDisplayNameClick,
  onEditCredentialsSettingClick,
  connectivityStatusUpdateTime,
  collapseStates,
  onToggleCollapse,
  components,
  onDragEnd,
  credentialsSection,
}) => {
  if (!deviceId) {
    return null;
  }

  // Simplified view mode
  if (viewMode === "simplified") {
    return (
      <div className="device-management-container simplified-view page-content">
        <LoadingModal isVisible={isSyncing} />
        <SimplifiedDeviceActions
          onExecuteAction={onExecuteAction}
          loadingAction={loadingAction}
          deviceState={deviceState}
        />
        <SimplifiedDeviceState
          deviceState={deviceState}
          connectivityStatusUpdateTime={connectivityStatusUpdateTime}
        />
      </div>
    );
  }

  // Detailed view mode with drag-and-drop support
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="components">
        {(provided) => (
          <div
            className="device-management-container detailed-view page-content"
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            <LoadingModal isVisible={isSyncing} />

            {components.map(({ id }, index) => {
              const shouldRender =
                (id === "deviceState" && deviceState) ||
                id === "lastActionStatus" ||
                id === "deviceActions" ||
                id === "deviceCredentials" ||
                (id === "deviceSettings" && deviceState);

              return shouldRender ? (
                <Draggable key={id} draggableId={id} index={index}>
                  {(providedDraggable) => (
                    <div
                      ref={providedDraggable.innerRef}
                      {...providedDraggable.draggableProps}
                    >
                      <hr className="content-divider" />
                      <section>
                        {id === "deviceState" && (
                          <DeviceState
                            deviceState={deviceState}
                            deviceId={deviceId}
                            isCollapsed={collapseStates.deviceState}
                            onToggleCollapse={onToggleCollapse.deviceState}
                            dragHandleProps={providedDraggable.dragHandleProps}
                            onEditDisplayNameClick={onEditDisplayNameClick}
                            onEditCredentialsSettingClick={onEditCredentialsSettingClick}
                            connectivityStatusUpdateTime={
                              connectivityStatusUpdateTime
                            }
                          />
                        )}
                        {id === "deviceActions" && (
                          <DeviceActions
                            isCollapsed={collapseStates.deviceActions}
                            onToggleCollapse={onToggleCollapse.deviceActions}
                            onExecuteAction={onExecuteAction}
                            loadingAction={loadingAction}
                            dragHandleProps={providedDraggable.dragHandleProps}
                            onDownloadSettings={onDownloadSettings}
                            onUpdateFirmware={onUpdateFirmware}
                            isDownloadingSettings={isDownloadingSettings}
                          />
                        )}
                        {id === "lastActionStatus" && (
                          <LastActionStatus
                            actionHistory={actionHistory}
                            limit={actionHistoryLimit}
                            onLimitChange={onActionHistoryLimitChange}
                            isCollapsed={collapseStates.lastAction}
                            onToggleCollapse={onToggleCollapse.lastAction}
                            dragHandleProps={providedDraggable.dragHandleProps}
                          />
                        )}
                        {id === "deviceCredentials" &&
                          credentialsSection &&
                          React.cloneElement(credentialsSection, {
                            dragHandleProps: providedDraggable.dragHandleProps,
                          })}
                        {id === "deviceSettings" && (
                          <DeviceSettings
                            deviceState={deviceState}
                            isCollapsed={collapseStates.deviceSettings}
                            onToggleCollapse={onToggleCollapse.deviceSettings}
                            dragHandleProps={providedDraggable.dragHandleProps}
                            onEditCredentialsSettingClick={onEditCredentialsSettingClick}
                          />
                        )}
                      </section>
                    </div>
                  )}
                </Draggable>
              ) : null;
            })}

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

DeviceManagementContainer.propTypes = {
  deviceId: PropTypes.string,
  deviceState: PropTypes.object,
  viewMode: PropTypes.oneOf(["simplified", "detailed"]).isRequired,
  actionHistory: PropTypes.array.isRequired,
  actionHistoryLimit: PropTypes.number.isRequired,
  onActionHistoryLimitChange: PropTypes.func.isRequired,
  loadingAction: PropTypes.string,
  onExecuteAction: PropTypes.func.isRequired,
  onDownloadSettings: PropTypes.func.isRequired,
  onUpdateFirmware: PropTypes.func,
  isDownloadingSettings: PropTypes.bool.isRequired,
  isSyncing: PropTypes.bool.isRequired,
  onEditDisplayNameClick: PropTypes.func.isRequired,
  onEditCredentialsSettingClick: PropTypes.func,
  connectivityStatusUpdateTime: PropTypes.number,
  collapseStates: PropTypes.shape({
    deviceState: PropTypes.bool,
    deviceActions: PropTypes.bool,
    lastAction: PropTypes.bool,
    deviceSettings: PropTypes.bool,
  }).isRequired,
  onToggleCollapse: PropTypes.shape({
    deviceState: PropTypes.func,
    deviceActions: PropTypes.func,
    lastAction: PropTypes.func,
    deviceSettings: PropTypes.func,
  }).isRequired,
  components: PropTypes.array,
  onDragEnd: PropTypes.func,
  credentialsSection: PropTypes.node,
};

export default DeviceManagementContainer;
